"use client"

import { useState, useEffect } from "react"
import { Menu, X, Home } from "lucide-react"
import { useLanguage } from "./LanguageContext"
import LanguageSwitcher from "./LanguageSwitcher"
import "./css/header-nav.css"
import "./css/mobile-menu.css"
import "./css/simple-footer.css"

interface NavigationProps {
  appConfig: any
  currentPage: "home" | "about" | "none"
  onNavigate: (page: "home" | "about") => void
  t: (path: string, defaultText: string) => string
}

const Navigation = ({ appConfig, currentPage, onNavigate, t }: NavigationProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { language } = useLanguage()

  // Handle window resize for mobile menu
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false)
      }
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [mobileMenuOpen])

  // Handle body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  // Handle navigation from mobile menu
  const handleNavigation = (page: "home" | "about") => {
    onNavigate(page)
    setMobileMenuOpen(false)
  }

  return (
    <>
      {/* Header with Logo and Navigation */}
      <header className="header">
        <div className="header-content">
          <div className="header-logo">
            <a href="/" className="header-logo-link">
              {appConfig?.branding?.logoLight && !appConfig.branding.logoLight.includes("undefined") ? (
                <img
                  src={appConfig.branding.logoLight || "/placeholder.svg"}
                  alt={appConfig?.branding?.logoAlt || "Logo"}
                  className="logo-image"
                />
              ) : (
                <div className="text-logo">{appConfig?.branding?.logoAlt || "Blinkli"}</div>
              )}
            </a>
          </div>

          {/* Navigation Links */}
          <div className="header-nav">
            <a href="/" className={`nav-link ${currentPage === "home" ? "active" : ""}`}>
              {t("interface.home", "Home")}
            </a>
            <a href="/about" className={`nav-link ${currentPage === "about" ? "active" : ""}`}>
              {t("interface.about", "About")}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div className={`nav-overlay ${mobileMenuOpen ? "open" : ""}`} onClick={toggleMobileMenu}>
        {/* Stop propagation to prevent closing when clicking inside the sidebar */}
        <div className={`nav-sidebar ${mobileMenuOpen ? "open" : ""}`} onClick={(e) => e.stopPropagation()}>
          <div className="nav-sidebar-header">
            <h2 className="nav-sidebar-title"></h2>
            <button className="nav-sidebar-close" onClick={toggleMobileMenu} aria-label="Close menu">
              <X size={24} />
            </button>
          </div>
          <div className="nav-sidebar-content">
            <a href="/" className={`nav-mobile-link ${currentPage === "home" ? "active" : ""}`}>
              <Home size={20} />
              {t("interface.home", "Home")}
            </a>

            <a href="/about" className={`nav-mobile-link ${currentPage === "about" ? "active" : ""}`}>
              {t("interface.about", "About")}
            </a>
          </div>
        </div>
      </div>
    </>
  )
}

export const Footer = ({ appConfig, t }: { appConfig: any; t: (path: string, defaultText: string) => string }) => {
  const { language } = useLanguage()

  return (
    <footer className="simple-footer">
      <div className="footer-container">
        <div className="footer-logo">
          {appConfig?.branding?.logo && !appConfig.branding.logo.includes("undefined") ? (
            <img
              src={appConfig.branding.logo || "/placeholder.svg"}
              alt={appConfig?.branding?.logoAlt || "Logo"}
              className="footer-logo-image"
            />
          ) : (
            <div className="footer-text-logo">{appConfig?.branding?.logoAlt || "Blinkli"}</div>
          )}
        </div>

        <div className="footer-copyright">
          <p>
            {appConfig?.footer?.copyright?.[language.code]?.replace("{year}", new Date().getFullYear()) ||
              `© ${new Date().getFullYear()} ${appConfig?.footer?.ownerName || "Blinkli"}. All rights reserved.`}
            &nbsp;{t("footer.copyrightText", "Created by")}{" "}
            <a href={appConfig?.footer?.learnMoreLink || "https://redfrontdoor.com"}>
              {" "}
              {appConfig?.footer?.ownerName || "Redfrontdoor"}
            </a>
          </p>
        </div>

        <div className="footer-language">
          <LanguageSwitcher />
        </div>
      </div>
    </footer>
  )
}

export default Navigation
